import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { deleteR2File, renameR2File } from 'src/actions/watcher/r2FilesActions';
import { R2ObjectInterface } from 'src/store/watcher/r2FilesSlice';
import { Input, message, Popconfirm, Tooltip } from 'antd';
import {
  FileOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import Wrapper from '../../../../componets/Wrapper';
import ListViewButtons from '../../../../componets/ListViewButtons';
import usePermissions from 'src/hooks/usePermissions';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'];

function getExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot !== -1 ? filename.slice(dot + 1).toLowerCase() : '';
}

function isImage(filename: string): boolean {
  return IMAGE_EXTENSIONS.includes(getExtension(filename));
}

function getFileIcon(filename: string) {
  const ext = getExtension(filename);

  if (['pdf'].includes(ext)) return <FilePdfOutlined />;
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FileZipOutlined />;
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return <PlayCircleOutlined />;
  if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(ext)) return <SoundOutlined />;
  if (['txt', 'md', 'csv', 'json', 'xml', 'yml', 'yaml'].includes(ext)) return <FileTextOutlined />;

  return <FileOutlined />;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

const FileThumbnails = () => {
  const dispatch = useAppDispatch();
  const objects = useAppSelector(state => state.watcher.r2Files.objects);
  const activeBucketId = useAppSelector(state => state.watcher.r2Files.activeBucketId);
  const { write_r2_files } = usePermissions();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  function startRename(record: R2ObjectInterface) {
    setEditingKey(record.key);
    setEditingName(record.filename);
  }

  function cancelRename() {
    setEditingKey(null);
    setEditingName('');
  }

  async function confirmRename(oldKey: string) {
    const trimmed = editingName.trim();
    if (!trimmed) {
      message.warning('Filename cannot be empty');
      return;
    }

    const resultAction = await dispatch(renameR2File({ key: oldKey, filename: trimmed, bucket_id: activeBucketId || undefined }));
    if (renameR2File.fulfilled.match(resultAction)) {
      message.success('File renamed');
      setEditingKey(null);
      setEditingName('');
    } else {
      message.error('Failed to rename file');
    }
  }

  async function handleDelete(key: string) {
    const resultAction = await dispatch(deleteR2File({ key, bucket_id: activeBucketId || undefined }));
    if (deleteR2File.fulfilled.match(resultAction)) {
      message.success('File deleted');
    } else {
      message.error('Failed to delete file');
    }
  }

  function handleCopyUrl(url: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    message.success('URL copied');
  }

  if (!objects.length) return null;

  return (

    <>
      <Wrapper type='type-1' border className='v-mb-10'>
        <ListViewButtons align='right' defaultType='list' />
      </Wrapper>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {objects.map((obj) => {
          const isEditing = editingKey === obj.key;

          return (
            <div
              key={obj.key}
              style={{
                width: 180,
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              {/* Preview area */}
              <div
                style={{
                  height: 140,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                  overflow: 'hidden',
                }}
              >
                {isImage(obj.filename) && obj.url ? (
                  <img
                    src={obj.url}
                    alt={obj.filename}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span style={{ fontSize: 48, color: '#999' }}>
                    {getFileIcon(obj.filename)}
                  </span>
                )}
              </div>

              {/* Info area */}
              <div style={{ padding: '8px 10px' }}>
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Input
                      size="small"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onPressEnter={() => confirmRename(obj.key)}
                      onKeyDown={e => e.key === 'Escape' && cancelRename()}
                      autoFocus
                    />
                    <CheckOutlined
                      style={{ color: '#52c41a', cursor: 'pointer', flexShrink: 0 }}
                      onClick={() => confirmRename(obj.key)}
                    />
                    <CloseOutlined
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                      onClick={cancelRename}
                    />
                  </div>
                ) : (
                  <Tooltip title={obj.filename}>
                    <div style={{
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {obj.filename}
                    </div>
                  </Tooltip>
                )}

                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {formatSize(obj.size)}
                </div>

                {/* Actions */}
                {!isEditing && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: 14 }}>
                    {obj.url && (
                      <Tooltip title="Copy URL">
                        <CopyOutlined
                          style={{ cursor: 'pointer', color: '#1890ff' }}
                          onClick={() => handleCopyUrl(obj.url!)}
                        />
                      </Tooltip>
                    )}
                    {write_r2_files && (
                      <>
                        <Tooltip title="Rename">
                          <EditOutlined
                            style={{ cursor: 'pointer' }}
                            onClick={() => startRename(obj)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete this file?"
                          onConfirm={() => handleDelete(obj.key)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Tooltip title="Delete">
                            <DeleteOutlined style={{ cursor: 'pointer', color: '#ff4d4f' }} />
                          </Tooltip>
                        </Popconfirm>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default FileThumbnails;
